import React, { useEffect, useRef } from "react";
import { RichText } from "prismic-reactjs";

import translate from "@deosite/helpers/langLibrary";

import Box from "@commerce-ui/core2/Box";
import Grid from "@commerce-ui/core2/Grid";
import Container from "@commerce-ui/core2/Container";

import PageContainer from "../../containers/UI/PageContainer";

import Image from "@deosite/components-new/Image";
import PhotoSlider from "../../components/PhotoSlider";
import MinifiedProductPanel from "../../components/MinifiedProductPanel";
import InspirationCard from "../../components/InspirationCard";

import SectionWithConfigurableHeader from "../../sections/SectionWithConfigurableHeader";
import Grid2Cards from "../../sections/Grid2Cards";

import ArrowRightIcon from "../../icons/arrow-next.svg";

import useAnalyticsAndCommerceTags from "@deosite/api/hooks/common/useAnalytics4AndCommerceTags";

const CollectionPage = props => {
  const {
    categoryProducts,
    footerContent,
    dataRequiredToHideLoader,
    currentCategory,
    prismicData,
    pagination,
    filtersString,
    addToCartDisabled,
    totalCount = ""
  } = props;

  const translations = {
    seoDescription: translate("Default seo description"),
    seoTitle: translate("Default seo title"),
    peopleLiked: translate("People also liked")
  };

  const { viewList } = useAnalyticsAndCommerceTags();
  const isFirstRun = useRef(true);

  const collectionName =
    filtersString + prismicData.content?.categoryName
      ? prismicData.content.categoryName
      : currentCategory.name;

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      viewList(categoryProducts, collectionName);
    }
  });

  const collectionImages = [];

  prismicData.content?.inspirationPage.length > 0 &&
    prismicData.content?.inspirationPage[0].images?.map(inspirationImage => {
      collectionImages.push(inspirationImage.image);
    });

  const { currentPage } = pagination.props;

  return (
    <PageContainer
      footerContent={footerContent}
      returnLinkLabel={null}
      onReturnLabelClick={null}
      minifiedMargin={true}
      metaTagsData={{
        description: translations.seoDescription,
        title: translations.seoTitle
      }}
      dataRequiredToHideLoader={dataRequiredToHideLoader}
    >
      <Container>
        <Grid cols={[1, 1, 20, 20, 20]}>
          {prismicData?.content?.inspirationPage ? (
            <Box
              sx={{
                gridColumn: ["span 2", null, "span 9", null]
              }}
            >
              <Box sx={{ display: ["block", null, "none"], pt: "s10" }}>
                <SectionWithConfigurableHeader
                  titleAs={"h1"}
                  title={collectionName}
                />
              </Box>
              <Box
                sx={{
                  position: ["relative", null, "sticky"],
                  top: ["s9", null, "100px", null, "10px"]
                }}
              >
                {collectionImages.length > 1 ? (
                  <PhotoSlider
                    slides={collectionImages}
                    buttonNext={<ArrowRightIcon />}
                    buttonPrevious={<ArrowRightIcon />}
                  />
                ) : (
                  collectionImages?.[0] && (
                    <Image
                      image={collectionImages?.[0]}
                      sx={{
                        width: "100%",
                        objectFit: "cover",
                        aspectRatio: 0.75,
                        height: ["410px", null, null, "814px"]
                      }}
                      sizes={
                        "(max-width: 1070px) and (min-width: 10px) 420px, 768px"
                      }
                    />
                  )
                )}
              </Box>
            </Box>
          ) : (
            <Box />
          )}
          <Box
            sx={{
              gridColumn: ["span 0", null, "span 1", null]
            }}
          />
          <Box
            sx={{
              gridColumn: ["span 2", null, "span 10", null],
              mt: ["s3", null, "s11"]
            }}
          >
            <Box sx={{ display: ["none", null, "block"] }}>
              <SectionWithConfigurableHeader
                titleAs={"h1"}
                title={
                  prismicData.content?.categoryName
                    ? `${prismicData.content.categoryName} (${totalCount})`
                    : `${currentCategory.name} (${totalCount})`
                }
              />
            </Box>
            <Box
              sx={{
                mb: "s10",
                mt: ["s10", null, null, "s11"]
              }}
            >
              {categoryProducts.map((product, index) => (
                <Box
                  sx={{ mt: ["s11", null, null, "0px"] }}
                  key={currentPage + "" + index}
                >
                  <MinifiedProductPanel
                    showOptionsSelector={false}
                    product={product}
                    addToCartDisabled={addToCartDisabled}
                  />
                </Box>
              ))}
            </Box>
            <Box sx={{ mb: "s10" }}>{pagination}</Box>
            {prismicData?.content?.description && (
              <Box sx={{ mt: ["s12", null, "0px"] }}>
                <Box
                  sx={{
                    borderTop: "1px",
                    borderTopColor: "neutral500",
                    borderStyle: "solid",
                    height: "1px"
                  }}
                />
                <Box
                  sx={{
                    font: "base",
                    color: "main",
                    pt: "s9",
                    pb: "s9",
                    h5: { font: "l", pt: "0px", pb: "0px", m: "0px" },
                    ul: { mb: "s0" },
                    p: { my: "s7" },
                    a: { color: "main", fontWeight: 700 }
                  }}
                >
                  {prismicData.content.description &&
                    RichText.render(prismicData.content.description.content)}
                </Box>

                <Box
                  sx={{
                    borderTop: "1px",
                    borderTopColor: "neutral500",
                    borderStyle: "solid",
                    height: "1px"
                  }}
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Container>
      {prismicData?.content?.inspirations?.length > 0 && (
        <SectionWithConfigurableHeader title={translations.peopleLiked}>
          <Container>
            <Grid2Cards>
              {prismicData.content.inspirations.map(
                (inspiration, index) =>
                  inspiration && (
                    <Box
                      sx={{
                        marginTop: index > 1 ? "s11" : 0,
                        width: "100%"
                      }}
                      key={index}
                    >
                      <InspirationCard inspiration={inspiration} key={index} />
                    </Box>
                  )
              )}
            </Grid2Cards>
          </Container>
        </SectionWithConfigurableHeader>
      )}
    </PageContainer>
  );
};

export default CollectionPage;
